/**
 * Extensible “insert onto existing rail” pipeline.
 *
 * Drop / connect flows that need to split a host primitive and splice in a
 * junction (or future merge/split) should go through here so geometry,
 * validation, and the replace transaction stay consistent.
 *
 * Supports Circular Junction (default 8 physical ports) on straights (split)
 * and on curves (replace the turn with a junction at the corner). Merge/split
 * payloads remain reserved.
 */
import { LayoutElement, ElementType, Dimensions, ToolboxItem } from '../types';
import {
  TOOLBOX_ITEMS,
  ELEMENT_COLORS,
  DEFAULT_TRACK_THICKNESS_METERS,
  DEFAULT_CURVE_RADIUS_METERS,
  DEFAULT_TRACK_DIRECTION,
} from '../constants';
import { generateId } from './geometry';
import { createEntityIdAllocator } from './entityIds';
import { computeSegmentBetween, ChainSizing } from './trackChain';
import {
  Pt,
  ENDPOINT_TOLERANCE,
  ALL_RAIL_TYPES,
  getRailEndpoints,
  projectPointToNearestRail,
  resolveRailType,
  isCurveRailType,
  isJunctionRailType,
  getAABB,
  getCurveSweepAngle,
  getCurveRadius,
  resolveTrackDirection,
} from './topology';
import { DEFAULT_LAYER_FOR_TYPE } from '../features/layers/layersSlice';
import { alignTrackToTravelHeading } from './trackDirectionMarker';

/** Matches `computeSegmentBetween`'s effective minimum straight length. */
export const MIN_INSERT_STRAIGHT_LENGTH = 1;

/** Default spoke count for junctions created by insert / Point Draw crossing. */
const INSERT_CIRCLE_JUNCTION_MAX_PORTS = 8;

/** How close (perp) the cursor must be to a centerline to count as a drop target. */
export const INSERT_HIT_PERP_DIST = 40;

/**
 * Keep a rebuilt stub’s travel direction aligned with the rail it was cut from.
 *
 * `computeSegmentBetween` canonicalizes axis-aligned straights to 0°/90°, which
 * would flip west/north uni hosts when a junction or track-node split replaces
 * them. Align after geometry so arrows and simulation match the original path.
 */
function inheritTravelDirection(
  el: LayoutElement,
  source: LayoutElement
): LayoutElement {
  return alignTrackToTravelHeading(el, source.rotation || 0);
}

export type InsertRejectReason =
  | 'not-on-rail'
  | 'on-curve'
  | 'on-junction'
  | 'on-endpoint'
  | 'too-short'
  | 'near-junction'
  | 'cannot-fit';

/** Future insert payloads share this discriminator. */
export type InsertPayloadKind =
  | 'circular-junction-through' // 4-port CJ, left↔right connected (v1)
  | 'circular-junction-branch' // future: 3/4-port with a new branch
  | 'merge-junction'
  | 'split-junction';

export type InsertOnRailOk = {
  ok: true;
  hostId: string;
  splitPoint: Pt;
  removeIds: string[];
  addElements: LayoutElement[];
  /** Prefer selecting the inserted junction after commit. */
  selectId: string;
};

export type InsertOnRailErr = {
  ok: false;
  reason: InsertRejectReason;
};

export type InsertOnRailResult = InsertOnRailOk | InsertOnRailErr;

export type PerpendicularCrossingInsertOk = InsertOnRailOk & {
  /** Point Draw must terminate at entryPort and restart at exitPort. */
  entryPort: Pt;
  exitPort: Pt;
  /**
   * True when the candidate end lies beyond the exit port (full cross).
   * False when the click lands on the host — only the approach stub is created
   * and Point Draw resumes from the unused opposite port.
   */
  continuesBeyond: boolean;
};

export type PerpendicularCrossingInsertResult =
  | PerpendicularCrossingInsertOk
  | InsertOnRailErr;

export type InsertOnRailOptions = {
  /** Perpendicular hit tolerance in world units. */
  hitPerpDist?: number;
  /** Junction footprint (defaults to toolbox Circle Junction size). */
  junctionDims?: Dimensions;
  /** Optional name factory for new elements. */
  nextName?: (type: ElementType) => string;
  /** Optional typed-id factory for new elements (TRACK-1, JUNCTION-1, …). */
  nextId?: (type: ElementType, railType?: string | null) => string;
  /** Existing ids used when `nextId` is omitted (layout + equipment). */
  existingIds?: string[];
  /** Meters helpers for straight customProperties (optional). */
  lengthMetersForWidth?: (widthWorld: number) => number;
};

/** Resolve a batch-safe nextId from options or from existingIds. */
function resolveNextId(
  options: InsertOnRailOptions
): (type: ElementType, railType?: string | null) => string {
  if (options.nextId) return options.nextId;
  const alloc = createEntityIdAllocator(options.existingIds ?? []);
  return (type, railType) => alloc.nextForElement(type, railType);
}

export type TrackNodePlacement = {
  host: LayoutElement;
  splitPoint: Pt;
};

const EPS = 1e-6;

const toolboxDefaults = (id: string) => {
  const item = TOOLBOX_ITEMS.find((i) => i.id === id);
  return {
    sku: item?.sku,
    defaultProperties: { ...(item?.defaultProperties ?? {}) },
    defaultDimensions: item?.defaultDimensions ?? { width: 42, height: 42 },
  };
};

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);

type Axis = 'horizontal' | 'vertical';
const PERPENDICULAR_ANGLE_TOLERANCE_DEG = 5;
const AXIS_SLOPE_TOLERANCE = Math.sin(
  (PERPENDICULAR_ANGLE_TOLERANCE_DEG * Math.PI) / 180
);

const segmentAxis = (a: Pt, b: Pt): Axis | null => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length <= EPS) return null;
  if (Math.abs(dy) / length <= AXIS_SLOPE_TOLERANCE) return 'horizontal';
  if (Math.abs(dx) / length <= AXIS_SLOPE_TOLERANCE) return 'vertical';
  return null;
};

export type CrossingSearchOptions = {
  /**
   * When set, the drawn leg may end short of the host: if the axis-aligned ray
   * from `start` through `end` hits a perpendicular straight and the click/`end`
   * is within this distance of that intersection, the hit still counts. Needed
   * because Point Draw snaps to the grid, which often misses DXF centerlines.
   */
  pullTolerance?: number;
};

/**
 * Finds the nearest existing straight crossed by an axis-aligned Point Draw leg.
 *
 * The intersection may lie in the interior of the drawn leg *or* at its end
 * (clicking onto an occupied centerline). With `pullTolerance`, a click that
 * snaps just short of the centerline still counts. Starting on the host,
 * host-endpoint contacts, parallel rails, curves, and junctions are excluded.
 * When several hosts qualify, the nearest along the approach wins.
 */
export function findPerpendicularStraightCrossing(
  start: Pt,
  end: Pt,
  elements: LayoutElement[],
  excludeIds: ReadonlySet<string> = new Set(),
  options: CrossingSearchOptions = {}
): { host: LayoutElement; intersection: Pt } | null {
  const drawAxis = segmentAxis(start, end);
  if (!drawAxis) return null;

  const drawVector = { x: end.x - start.x, y: end.y - start.y };
  const drawLength = Math.hypot(drawVector.x, drawVector.y);
  if (drawLength <= EPS) return null;
  const pullTol = Math.max(0, options.pullTolerance ?? 0);
  const candidates: { host: LayoutElement; intersection: Pt; drawT: number }[] = [];

  for (const host of elements) {
    if (
      excludeIds.has(host.id) ||
      !host.metadata.visible ||
      resolveRailType(host) !== 'straight'
    ) {
      continue;
    }

    const hostEnds = getRailEndpoints(host);
    if (hostEnds.length < 2) continue;
    const hostAxis = segmentAxis(hostEnds[0], hostEnds[1]);
    if (!hostAxis || hostAxis === drawAxis) continue;

    const hostVector = {
      x: hostEnds[1].x - hostEnds[0].x,
      y: hostEnds[1].y - hostEnds[0].y,
    };
    const hostLength = Math.hypot(hostVector.x, hostVector.y);
    if (hostLength <= EPS) continue;
    const perpendicularCosine =
      Math.abs(drawVector.x * hostVector.x + drawVector.y * hostVector.y) /
      (drawLength * hostLength);
    if (perpendicularCosine > AXIS_SLOPE_TOLERANCE) continue;

    const denominator =
      drawVector.x * hostVector.y - drawVector.y * hostVector.x;
    if (Math.abs(denominator) <= EPS) continue;
    const offset = {
      x: hostEnds[0].x - start.x,
      y: hostEnds[0].y - start.y,
    };
    const drawT =
      (offset.x * hostVector.y - offset.y * hostVector.x) / denominator;
    const hostT =
      (offset.x * drawVector.y - offset.y * drawVector.x) / denominator;
    // Keep the approach off the host. Allow drawT ≈ 1 (click on host) and, when
    // pullTolerance is set, drawT > 1 if the click is still near the intersection
    // (grid snap landed short of a DXF centerline).
    const drawStartMargin = ENDPOINT_TOLERANCE / drawLength;
    const hostMargin = ENDPOINT_TOLERANCE / hostLength;
    if (drawT <= drawStartMargin || hostT <= hostMargin || hostT >= 1 - hostMargin) {
      continue;
    }

    const intersection = {
      x: start.x + drawVector.x * drawT,
      y: start.y + drawVector.y * drawT,
    };
    const onOrBeforeEnd = drawT <= 1 + EPS;
    const pulledToHost =
      pullTol > 0 && drawT > 1 && dist(end, intersection) <= pullTol;
    if (!onOrBeforeEnd && !pulledToHost) continue;

    candidates.push({ host, intersection, drawT });
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.drawT - b.drawT);
  return { host: candidates[0].host, intersection: candidates[0].intersection };
}

const rotatePt = (deg: number, x: number, y: number): Pt => {
  const r = (deg * Math.PI) / 180;
  return { x: x * Math.cos(r) - y * Math.sin(r), y: x * Math.sin(r) + y * Math.cos(r) };
};

/** Acute angle (0–90°) between two undirected axes. */
const axisOffsetDeg = (a: number, b: number): number => {
  const d = normDeg(a - b) % 180;
  return Math.min(d, 180 - d);
};

/** Angular errors below this count as equal when ranking candidate rotations. */
const CIRCLE_JUNCTION_TIE_EPS = 1e-6;

/**
 * Best-fit position/rotation for a circular junction so opposite spokes face
 * `legs[0]` and `legs[1]` (the through pair). Same approach as the auto-router.
 */
function solveCircleJunction(
  legs: Pt[],
  dims: Dimensions,
  maxPorts: number
): { position: Pt; rotation: number; legOrder: number[] } {
  const cx = legs.length >= 2 ? (legs[0].x + legs[1].x) / 2 : legs[0].x;
  const cy = legs.length >= 2 ? (legs[0].y + legs[1].y) / 2 : legs[0].y;

  // An 8-way hub has spokes every 45°, so every rotation that is a multiple of
  // 45 seats the through pair exactly and scores the same angular error. Rank
  // those ties by how square the hub sits with the travel axis, otherwise
  // float noise in atan2 decides and a hub on a horizontal rail can come out
  // at 45° — which also tips its turntable bar onto a diagonal.
  const axisDeg =
    legs.length >= 2
      ? (Math.atan2(legs[1].y - legs[0].y, legs[1].x - legs[0].x) * 180) / Math.PI
      : (Math.atan2(legs[0].y - cy, legs[0].x - cx) * 180) / Math.PI;

  const candidateRotations = Array.from({ length: 24 }, (_, i) => i * 15);
  let best: {
    position: Pt;
    rotation: number;
    err: number;
    legOrder: number[];
    axisErr: number;
  } | null = null;

  for (const rotation of candidateRotations) {
    const centerOffset = rotatePt(rotation, dims.width / 2, dims.height / 2);
    const position = { x: cx - centerOffset.x, y: cy - centerOffset.y };
    const probe: LayoutElement = {
      id: '__probe__',
      layerId: 'layer-1',
      type: 'cross-rail',
      position,
      rotation,
      dimensions: dims,
      metadata: { name: '', locked: false, visible: true },
      customProperties: { railType: 'junction-circle', maxPorts },
    };
    const spokeTips = getRailEndpoints(probe);

    const used = new Set<number>();
    const legOrder: number[] = [];
    let err = 0;
    for (const leg of legs) {
      let bestIdx = -1;
      let bestDist = Infinity;
      spokeTips.forEach((tip, idx) => {
        if (used.has(idx)) return;
        const angleToLeg = Math.atan2(leg.y - cy, leg.x - cx);
        const angleToTip = Math.atan2(tip.y - cy, tip.x - cx);
        let angleDiff = Math.abs(angleToLeg - angleToTip);
        if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
        if (angleDiff < bestDist) {
          bestDist = angleDiff;
          bestIdx = idx;
        }
      });
      used.add(bestIdx);
      legOrder.push(bestIdx);
      err += bestDist;
    }
    const axisErr = axisOffsetDeg(rotation, axisDeg);
    const better =
      !best ||
      err < best.err - CIRCLE_JUNCTION_TIE_EPS ||
      (err <= best.err + CIRCLE_JUNCTION_TIE_EPS && axisErr < best.axisErr);
    if (better) best = { position, rotation, err, legOrder, axisErr };
  }

  return {
    position: best!.position,
    rotation: best!.rotation,
    legOrder: best!.legOrder,
  };
}

const normDeg = (deg: number) => ((deg % 360) + 360) % 360;

/**
 * Unit direction from `from` to `to`, snapped to exact horizontal/vertical when
 * it is within the axis tolerance. Snapping matters because rebuilt rails are
 * generated along this vector: a residual 1e-3 tilt would store a diagonal rail
 * instead of a Manhattan one.
 */
function axisDirection(from: Pt, to: Pt): Pt | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length <= EPS) return null;
  const ux = dx / length;
  const uy = dy / length;
  if (Math.abs(uy) <= AXIS_SLOPE_TOLERANCE) return { x: Math.sign(ux), y: 0 };
  if (Math.abs(ux) <= AXIS_SLOPE_TOLERANCE) return { x: 0, y: Math.sign(uy) };
  return { x: ux, y: uy };
}

/** Outward tangent at each curve endpoint (matches Point Draw / Canvas). */
function curveEndpointTangents(el: LayoutElement): { pt: Pt; outwardDeg: number }[] {
  const rt = resolveRailType(el);
  const pts = getRailEndpoints(el);
  if (!isCurveRailType(rt) || pts.length < 2) return [];
  const rot = normDeg(el.rotation || 0);
  const theta = getCurveSweepAngle(rt);
  return [
    { pt: pts[0], outwardDeg: normDeg(270 + rot) },
    { pt: pts[1], outwardDeg: normDeg(theta + 90 + rot) },
  ];
}

/** Intersection of two infinite axes, or null when they are parallel. */
function intersectAxes(pA: Pt, dirA: Pt, pB: Pt, dirB: Pt): Pt | null {
  const den = dirA.x * dirB.y - dirA.y * dirB.x;
  if (Math.abs(den) <= EPS) return null;
  const dx = pB.x - pA.x;
  const dy = pB.y - pA.y;
  const t = (dx * dirB.y - dy * dirB.x) / den;
  return { x: pA.x + dirA.x * t, y: pA.y + dirA.y * t };
}

/**
 * Manhattan corner of a curve turn: intersection of the two endpoint tangent
 * axes. Null when the axes are parallel (typical of curve180 U-turns).
 */
export function curveCornerVertex(el: LayoutElement): Pt | null {
  const tangents = curveEndpointTangents(el);
  if (tangents.length < 2) return null;
  const [a, b] = tangents;
  const aRad = (a.outwardDeg * Math.PI) / 180;
  const bRad = (b.outwardDeg * Math.PI) / 180;
  const ax = Math.cos(aRad);
  const ay = Math.sin(aRad);
  const bx = Math.cos(bRad);
  const by = Math.sin(bRad);
  const den = ax * by - ay * bx;
  if (Math.abs(den) <= EPS) return null;
  const dx = b.pt.x - a.pt.x;
  const dy = b.pt.y - a.pt.y;
  const t = (dx * by - dy * bx) / den;
  return { x: a.pt.x + ax * t, y: a.pt.y + ay * t };
}

export type CurveTurnHit = {
  host: LayoutElement;
  /** Manhattan corner the operator sees as "the turn". */
  corner: Pt;
  /** Distance from the query point to the corner or the arc, whichever is nearer. */
  distance: number;
};

/**
 * Nearest curve turn to `worldPt`, measured against both the corner vertex and
 * the arc itself.
 *
 * Fillet arcs are short (a fraction of a grid at typical scales), so requiring a
 * hit on the arc centerline makes turns almost unclickable. Measuring to the
 * corner lets callers offer the turn with a normal snap radius.
 */
export function findCurveTurnNear(
  worldPt: Pt,
  elements: LayoutElement[],
  maxDist: number,
  excludeIds: ReadonlySet<string> = new Set()
): CurveTurnHit | null {
  let best: CurveTurnHit | null = null;

  for (const el of elements) {
    if (excludeIds.has(el.id) || !el.metadata.visible) continue;
    if (!isCurveRailType(resolveRailType(el))) continue;

    const corner = curveCornerVertex(el);
    if (!corner) continue;

    const arc = projectPointToNearestRail([el], worldPt, maxDist);
    const distance = Math.min(
      dist(worldPt, corner),
      arc ? arc.perpDist : Infinity
    );
    if (distance > maxDist) continue;
    if (!best || distance < best.distance) best = { host: el, corner, distance };
  }

  return best;
}

type AbuttingStraight = {
  rail: LayoutElement;
  nearEnd: Pt;
  farEnd: Pt;
};

/**
 * Straight whose end sits closest to `tip` within `tolerance` (not the host
 * curve itself, and not a rail already claimed by the other leg).
 */
function findAbuttingStraight(
  tip: Pt,
  hostId: string,
  elements: LayoutElement[],
  tolerance: number,
  claimedIds: ReadonlySet<string> = new Set()
): AbuttingStraight | null {
  let best: (AbuttingStraight & { distance: number }) | null = null;

  for (const el of elements) {
    if (el.id === hostId || claimedIds.has(el.id) || !el.metadata.visible) continue;
    if (resolveRailType(el) !== 'straight') continue;
    const ends = getRailEndpoints(el);
    if (ends.length < 2) continue;

    const candidates: AbuttingStraight[] = [
      { rail: el, nearEnd: ends[0], farEnd: ends[1] },
      { rail: el, nearEnd: ends[1], farEnd: ends[0] },
    ];
    for (const candidate of candidates) {
      const distance = dist(candidate.nearEnd, tip);
      if (distance > tolerance) continue;
      if (!best || distance < best.distance) best = { ...candidate, distance };
    }
  }

  return best;
}

const defaultJunctionDims = (): Dimensions => {
  const { defaultDimensions } = toolboxDefaults('rail-circle-junction');
  return {
    width: defaultDimensions.width,
    height: defaultDimensions.height,
  };
};

const nearExistingJunction = (
  split: Pt,
  hostId: string,
  elements: LayoutElement[],
  clearance: number
): boolean => {
  for (const el of elements) {
    if (el.id === hostId || !el.metadata.visible) continue;
    if (!isJunctionRailType(resolveRailType(el))) continue;
    const box = getAABB(el);
    const cx = (box.minX + box.maxX) / 2;
    const cy = (box.minY + box.maxY) / 2;
    if (dist(split, { x: cx, y: cy }) <= clearance) return true;
    for (const tip of getRailEndpoints(el)) {
      if (dist(split, tip) <= clearance) return true;
    }
  }
  return false;
};

/**
 * Classify what (if anything) under `worldPt` can host an insert.
 * Pure detection — does not build geometry.
 */
export function findInsertHost(
  worldPt: Pt,
  elements: LayoutElement[],
  hitPerpDist: number = INSERT_HIT_PERP_DIST
):
  | { kind: 'straight'; host: LayoutElement; split: Pt; along: number; length: number }
  | { kind: 'curve'; host: LayoutElement; split: Pt }
  | { kind: 'junction'; host: LayoutElement; split: Pt }
  | null {
  const rails = elements.filter((el) => el.metadata.visible && ALL_RAIL_TYPES.has(el.type));
  const hit = projectPointToNearestRail(rails, worldPt, hitPerpDist);
  if (!hit) return null;

  const host = elements.find((el) => el.id === hit.railId);
  if (!host) return null;

  const rt = resolveRailType(host);
  const split = { x: hit.x, y: hit.y };

  if (isJunctionRailType(rt)) return { kind: 'junction', host, split };
  if (isCurveRailType(rt)) return { kind: 'curve', host, split };
  if (rt !== 'straight') return null;

  const ends = getRailEndpoints(host);
  if (ends.length < 2) return null;
  const length = dist(ends[0], ends[1]);
  return { kind: 'straight', host, split, along: hit.distance, length };
}

/**
 * Resolve a valid click-to-place track node location. Manual nodes are
 * represented by splitting a straight rail, so the normal graph builder,
 * persistence, and AMHS export all see the new shared endpoint.
 */
export function findTrackNodePlacement(
  worldPt: Pt,
  elements: LayoutElement[],
  hitPerpDist: number = INSERT_HIT_PERP_DIST
): TrackNodePlacement | null {
  const hostInfo = findInsertHost(worldPt, elements, hitPerpDist);
  if (!hostInfo || hostInfo.kind !== 'straight') return null;

  const ends = getRailEndpoints(hostInfo.host);
  if (ends.length < 2) return null;
  const [endA, endB] = ends;
  const splitPoint = hostInfo.split;
  if (
    dist(endA, splitPoint) <= ENDPOINT_TOLERANCE ||
    dist(splitPoint, endB) <= ENDPOINT_TOLERANCE ||
    dist(endA, splitPoint) < MIN_INSERT_STRAIGHT_LENGTH ||
    dist(splitPoint, endB) < MIN_INSERT_STRAIGHT_LENGTH
  ) {
    return null;
  }

  return { host: hostInfo.host, splitPoint };
}

/**
 * Names for the two pieces a host straight is cut into. The piece at the
 * original start keeps the host's name so the user still recognises the track;
 * the other piece must take a fresh name, otherwise both halves would render
 * with the same label.
 */
function resolveSplitNames(
  host: LayoutElement,
  nextName?: (type: ElementType) => string
): [string, string] {
  const hostName = (host.metadata.name || '').trim();
  const firstName = hostName || nextName?.('overhead-rail') || 'Rail';
  const secondName = nextName?.('overhead-rail') ?? `${firstName} B`;
  return [firstName, secondName === firstName ? `${firstName} B` : secondName];
}

function lengthMetersForResizedStraight(
  source: LayoutElement,
  width: number,
  override?: (widthWorld: number) => number
): number {
  if (override) return override(width);
  const sourceLengthMeters = Number(source.customProperties?.lengthMeters);
  const sourceWidth = source.dimensions.width || 1;
  return Number.isFinite(sourceLengthMeters) && sourceLengthMeters > 0 && sourceWidth > 0
    ? (sourceLengthMeters * width) / sourceWidth
    : width;
}

/** Rebuild one straight while preserving its identity-related rail properties. */
function rebuildStraightBetween(
  source: LayoutElement,
  from: Pt,
  to: Pt,
  id: string,
  lengthMetersForWidth?: (widthWorld: number) => number
): LayoutElement {
  const thickness = Math.max(0.001, source.dimensions.height || 4);
  const geom = computeSegmentBetween(from, to, {
    lengthUnits: 1,
    thicknessUnits: thickness,
    radiusUnits: thickness,
  });
  return inheritTravelDirection(
    {
      ...source,
      id,
      position: { ...geom.position, z: source.position.z },
      rotation: geom.rotation,
      dimensions: { ...geom.dimensions, depth: source.dimensions.depth },
      metadata: { ...source.metadata, locked: false },
      customProperties: {
        ...source.customProperties,
        railType: 'straight',
        lengthMeters: lengthMetersForResizedStraight(
          source,
          geom.dimensions.width,
          lengthMetersForWidth
        ),
      },
    },
    source
  );
}

function completePortConnections(portIds: string[]) {
  const connections: { id: string; fromPortId: string; toPortId: string }[] = [];
  for (const fromPortId of portIds) {
    for (const toPortId of portIds) {
      if (fromPortId !== toPortId) {
        connections.push({ id: generateId(), fromPortId, toPortId });
      }
    }
  }
  return connections;
}

/** Plan a manual network-node insertion by replacing one straight with two. */
export function planTrackNodeInsertOnStraight(
  host: LayoutElement,
  splitPoint: Pt,
  options: InsertOnRailOptions = {}
): InsertOnRailResult {
  if (resolveRailType(host) !== 'straight') {
    return { ok: false, reason: 'on-curve' };
  }

  const ends = getRailEndpoints(host);
  if (ends.length < 2) return { ok: false, reason: 'not-on-rail' };
  const [endA, endB] = ends;
  const vx = endB.x - endA.x;
  const vy = endB.y - endA.y;
  const len2 = vx * vx + vy * vy;
  if (len2 < EPS) return { ok: false, reason: 'too-short' };

  const t = Math.max(
    0,
    Math.min(
      1,
      ((splitPoint.x - endA.x) * vx + (splitPoint.y - endA.y) * vy) / len2
    )
  );
  const split = { x: endA.x + vx * t, y: endA.y + vy * t };
  const lengthA = dist(endA, split);
  const lengthB = dist(split, endB);
  if (lengthA <= ENDPOINT_TOLERANCE || lengthB <= ENDPOINT_TOLERANCE) {
    return { ok: false, reason: 'on-endpoint' };
  }
  if (
    lengthA < MIN_INSERT_STRAIGHT_LENGTH ||
    lengthB < MIN_INSERT_STRAIGHT_LENGTH
  ) {
    return { ok: false, reason: 'too-short' };
  }

  const hostProps = host.customProperties || {};
  const lengthMetersForWidth =
    options.lengthMetersForWidth ??
    ((width: number) => {
      const hostLengthMeters = Number(hostProps.lengthMeters);
      const hostWidth = host.dimensions.width || 1;
      return Number.isFinite(hostLengthMeters) && hostLengthMeters > 0 && hostWidth > 0
        ? (hostLengthMeters * width) / hostWidth
        : width;
    });
  const [firstName, secondName] = resolveSplitNames(host, options.nextName);
  const nextId = resolveNextId(options);
  const sizing: ChainSizing = {
    lengthUnits: 1,
    thicknessUnits: Math.max(0.001, host.dimensions.height || 4),
    radiusUnits: Math.max(0.001, host.dimensions.height || 4),
  };

  const makeStraight = (from: Pt, to: Pt, name: string): LayoutElement => {
    const geom = computeSegmentBetween(from, to, sizing);
    return inheritTravelDirection(
      {
        ...host,
        id: nextId('overhead-rail', 'straight'),
        position: { ...geom.position, z: host.position.z },
        rotation: geom.rotation,
        dimensions: { ...geom.dimensions, depth: host.dimensions.depth },
        metadata: {
          ...host.metadata,
          name,
        },
        customProperties: {
          ...hostProps,
          railType: 'straight',
          lengthMeters: lengthMetersForWidth(geom.dimensions.width),
        },
      },
      host
    );
  };

  const first = makeStraight(endA, split, firstName);
  const second = makeStraight(split, endB, secondName);
  return {
    ok: true,
    hostId: host.id,
    splitPoint: split,
    removeIds: [host.id],
    addElements: [first, second],
    selectId: first.id,
  };
}

export function tryInsertTrackNodeOnRail(
  worldPt: Pt,
  elements: LayoutElement[],
  options: InsertOnRailOptions = {}
): InsertOnRailResult {
  const hostInfo = findInsertHost(
    worldPt,
    elements,
    options.hitPerpDist ?? INSERT_HIT_PERP_DIST
  );
  if (!hostInfo) return { ok: false, reason: 'not-on-rail' };
  if (hostInfo.kind === 'curve') return { ok: false, reason: 'on-curve' };
  if (hostInfo.kind === 'junction') return { ok: false, reason: 'on-junction' };
  return planTrackNodeInsertOnStraight(hostInfo.host, hostInfo.split, {
    ...options,
    existingIds: options.existingIds ?? elements.map((el) => el.id),
  });
}

export type RemoveTrackNodeOk = {
  ok: true;
  removeIds: string[];
  addElements: LayoutElement[];
  selectId: string;
  point: Pt;
};

export type RemoveTrackNodeErr = {
  ok: false;
  reason: 'not-mergeable';
};

export type RemoveTrackNodeResult = RemoveTrackNodeOk | RemoveTrackNodeErr;

/**
 * Reverse of planTrackNodeInsertOnStraight: merge two collinear straight rails
 * that meet at `point` back into one continuous rail (removes the mid track node).
 */
export function planRemoveTrackNode(
  point: Pt,
  elements: LayoutElement[],
  options: InsertOnRailOptions = {}
): RemoveTrackNodeResult {
  type Hit = { el: LayoutElement; far: Pt };
  const hits: Hit[] = [];

  for (const el of elements) {
    if (resolveRailType(el) !== 'straight') continue;
    const ends = getRailEndpoints(el);
    if (ends.length < 2) continue;
    const [a, b] = ends;
    if (dist(a, point) <= ENDPOINT_TOLERANCE) hits.push({ el, far: b });
    else if (dist(b, point) <= ENDPOINT_TOLERANCE) hits.push({ el, far: a });
  }

  if (hits.length !== 2 || hits[0].el.id === hits[1].el.id) {
    return { ok: false, reason: 'not-mergeable' };
  }

  const [h0, h1] = hits;
  const v0 = { x: h0.far.x - point.x, y: h0.far.y - point.y };
  const v1 = { x: h1.far.x - point.x, y: h1.far.y - point.y };
  const len0 = Math.hypot(v0.x, v0.y);
  const len1 = Math.hypot(v1.x, v1.y);
  if (len0 < EPS || len1 < EPS) return { ok: false, reason: 'not-mergeable' };

  // Must be collinear and extend in opposite directions (true mid-node).
  const cross = v0.x * v1.y - v0.y * v1.x;
  if (Math.abs(cross) > ENDPOINT_TOLERANCE * Math.max(len0, len1)) {
    return { ok: false, reason: 'not-mergeable' };
  }
  if (v0.x * v1.x + v0.y * v1.y >= 0) {
    return { ok: false, reason: 'not-mergeable' };
  }

  const host = len0 >= len1 ? h0.el : h1.el;
  const hostProps = host.customProperties || {};
  const lengthMetersForWidth =
    options.lengthMetersForWidth ??
    ((width: number) => {
      const hostLengthMeters = Number(hostProps.lengthMeters);
      const hostWidth = host.dimensions.width || 1;
      return Number.isFinite(hostLengthMeters) && hostLengthMeters > 0 && hostWidth > 0
        ? (hostLengthMeters * width) / hostWidth
        : width;
    });
  const sizing: ChainSizing = {
    lengthUnits: 1,
    thicknessUnits: Math.max(0.001, host.dimensions.height || 4),
    radiusUnits: Math.max(0.001, host.dimensions.height || 4),
  };
  const geom = computeSegmentBetween(h0.far, h1.far, sizing);
  const name =
    options.nextName?.('overhead-rail') ??
    host.metadata?.name ??
    'Rail';
  const nextId = resolveNextId({
    ...options,
    existingIds: options.existingIds ?? elements.map((el) => el.id),
  });

  const merged: LayoutElement = inheritTravelDirection(
    {
      ...host,
      id: nextId('overhead-rail', 'straight'),
      position: { ...geom.position, z: host.position.z },
      rotation: geom.rotation,
      dimensions: { ...geom.dimensions, depth: host.dimensions.depth },
      metadata: {
        ...host.metadata,
        name,
      },
      customProperties: {
        ...hostProps,
        railType: 'straight',
        lengthMeters: lengthMetersForWidth(geom.dimensions.width),
      },
    },
    host
  );

  return {
    ok: true,
    removeIds: [h0.el.id, h1.el.id],
    addElements: [merged],
    selectId: merged.id,
    point,
  };
}

/**
 * Plan a through Circular Junction insert onto a straight host.
 * Returns the remove/add set for a single history `replace`, or a reject reason.
 */
export function planCircularJunctionInsertOnStraight(
  host: LayoutElement,
  splitPoint: Pt,
  elements: LayoutElement[],
  options: InsertOnRailOptions = {}
): InsertOnRailResult {
  if (resolveRailType(host) !== 'straight') {
    return { ok: false, reason: 'on-curve' };
  }

  const ends = getRailEndpoints(host);
  if (ends.length < 2) return { ok: false, reason: 'not-on-rail' };
  const [endA, endB] = ends;

  const dims = options.junctionDims ?? defaultJunctionDims();
  const half = Math.max(dims.width, dims.height) / 2;
  const minStub = MIN_INSERT_STRAIGHT_LENGTH;

  const totalLen = dist(endA, endB);
  if (totalLen < half * 2 + minStub * 2) {
    return { ok: false, reason: 'cannot-fit' };
  }

  // Snap split onto the true centerline (collinear projection).
  const vx = endB.x - endA.x;
  const vy = endB.y - endA.y;
  const len2 = vx * vx + vy * vy;
  if (len2 < EPS) return { ok: false, reason: 'too-short' };
  const t = Math.max(0, Math.min(1, ((splitPoint.x - endA.x) * vx + (splitPoint.y - endA.y) * vy) / len2));
  const S = { x: endA.x + vx * t, y: endA.y + vy * t };

  const distA = dist(endA, S);
  const distB = dist(S, endB);

  if (distA <= ENDPOINT_TOLERANCE || distB <= ENDPOINT_TOLERANCE) {
    return { ok: false, reason: 'on-endpoint' };
  }
  if (distA < half + minStub || distB < half + minStub) {
    return { ok: false, reason: 'cannot-fit' };
  }

  const clearance = half + ENDPOINT_TOLERANCE;
  if (nearExistingJunction(S, host.id, elements, clearance)) {
    return { ok: false, reason: 'near-junction' };
  }

  const dirLen = Math.hypot(vx, vy) || 1;
  const dir = { x: vx / dirLen, y: vy / dirLen };
  // Provisional through targets at ±half along the rail — used to orient the CJ.
  const throughTargetA = { x: S.x - dir.x * half, y: S.y - dir.y * half };
  const throughTargetB = { x: S.x + dir.x * half, y: S.y + dir.y * half };

  const maxPorts = INSERT_CIRCLE_JUNCTION_MAX_PORTS; // 8-way hub; unused spokes stay hidden
  const { position, rotation, legOrder } = solveCircleJunction(
    [throughTargetA, throughTargetB],
    dims,
    maxPorts
  );

  const { sku, defaultProperties } = toolboxDefaults('rail-circle-junction');
  const layerId = host.layerId || DEFAULT_LAYER_FOR_TYPE['cross-rail'] || 'layer-1';
  const nextName =
    options.nextName ??
    ((type: ElementType) => (type === 'cross-rail' ? 'Circle Junction' : 'Rail'));
  const nextId = resolveNextId({
    ...options,
    existingIds: options.existingIds ?? elements.map((el) => el.id),
  });

  const hostProps = host.customProperties || {};
  const lengthMetersForWidth =
    options.lengthMetersForWidth ??
    ((w: number) => {
      const hostLen = Number(hostProps.lengthMeters);
      const hostW = host.dimensions.width || 1;
      if (Number.isFinite(hostLen) && hostLen > 0 && hostW > 0) {
        return (hostLen * w) / hostW;
      }
      return w;
    });

  const connections: { id: string; fromPortId: string; toPortId: string }[] = [];
  const activePortIds = legOrder.slice(0, 2).map((index) => `port-${index}`);
  // Wire only the through pair (left ↔ right). Top/bottom ports stay unused.
  if (legOrder.length >= 2) {
    const a = legOrder[0];
    const b = legOrder[1];
    connections.push(
      { id: generateId(), fromPortId: `port-${a}`, toPortId: `port-${b}` },
      { id: generateId(), fromPortId: `port-${b}`, toPortId: `port-${a}` }
    );
  }

  const junctionId = nextId('cross-rail', 'junction-circle');
  const junction: LayoutElement = {
    id: junctionId,
    type: 'cross-rail',
    layerId,
    position: { ...position, z: host.position.z },
    rotation,
    dimensions: { ...dims, depth: host.dimensions.depth },
    metadata: {
      name: nextName('cross-rail'),
      locked: false,
      visible: true,
      color: ELEMENT_COLORS['cross-rail'],
      layerId,
    },
    customProperties: {
      ...defaultProperties,
      sku,
      railType: 'junction-circle',
      maxPorts,
      junctionInputs: 2,
      junctionOutputs: 2,
      activePortIds,
      connections,
      thicknessMeters: hostProps.thicknessMeters ?? DEFAULT_TRACK_THICKNESS_METERS,
      radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
    },
  };

  // Stubs meet the *actual* spoke tips (not the provisional ±half targets) so
  // there are no gaps after the angular best-fit placement.
  const spokeTips = getRailEndpoints(junction);
  const portA = spokeTips[legOrder[0]] ?? throughTargetA;
  const portB = spokeTips[legOrder[1]] ?? throughTargetB;

  // Assign each port to the nearer original end so left/right stubs stay correct
  // regardless of legOrder orientation.
  const pairForA =
    dist(endA, portA) <= dist(endA, portB)
      ? { inner: portA, other: portB }
      : { inner: portB, other: portA };

  const leftLen = dist(endA, pairForA.inner);
  const rightLen = dist(pairForA.other, endB);
  if (leftLen < minStub || rightLen < minStub) {
    return { ok: false, reason: 'too-short' };
  }

  const thickness = Math.max(0.001, host.dimensions.height || 4);
  const sizing: ChainSizing = {
    lengthUnits: 1,
    thicknessUnits: thickness,
    radiusUnits: thickness,
  };

  const leftGeom = computeSegmentBetween(endA, pairForA.inner, sizing);
  const rightGeom = computeSegmentBetween(pairForA.other, endB, sizing);

  const [leftName, rightName] = resolveSplitNames(host, options.nextName);

  const makeStraight = (
    geom: ReturnType<typeof computeSegmentBetween>,
    name: string
  ): LayoutElement =>
    inheritTravelDirection(
      {
        id: nextId('overhead-rail', 'straight'),
        type: 'overhead-rail',
        layerId: host.layerId,
        position: { ...geom.position, z: host.position.z },
        rotation: geom.rotation,
        dimensions: {
          ...geom.dimensions,
          depth: host.dimensions.depth,
        },
        metadata: {
          name,
          locked: false,
          visible: true,
          color: host.metadata.color || ELEMENT_COLORS['overhead-rail'],
          layerId: host.layerId,
        },
        customProperties: {
          sku: hostProps.sku ?? 'AeroTrak',
          railType: 'straight',
          direction: hostProps.direction ?? DEFAULT_TRACK_DIRECTION,
          heightM: hostProps.heightM ?? 3.2,
          thicknessMeters: hostProps.thicknessMeters ?? DEFAULT_TRACK_THICKNESS_METERS,
          lengthMeters: lengthMetersForWidth(geom.dimensions.width),
        },
      },
      host
    );

  const leftStraight = makeStraight(leftGeom, leftName);
  const rightStraight = makeStraight(rightGeom, rightName);

  return {
    ok: true,
    hostId: host.id,
    splitPoint: S,
    removeIds: [host.id],
    addElements: [leftStraight, junction, rightStraight],
    selectId: junctionId,
  };
}

type CollinearRun = {
  segments: LayoutElement[];
  vertices: Pt[];
};

/**
 * Collect the visually continuous straight line containing `host`.
 *
 * Growth stops at a branch, curve, junction, or ambiguous node, so absorbing
 * the internal split nodes cannot disconnect any third track.
 */
function collectCollinearRun(
  host: LayoutElement,
  elements: LayoutElement[]
): CollinearRun | null {
  if (resolveRailType(host) !== 'straight') return null;
  const hostEnds = getRailEndpoints(host);
  if (hostEnds.length < 2) return null;
  const axis = axisDirection(hostEnds[0], hostEnds[1]);
  if (!axis) return null;

  const rails = elements
    .filter((element) => ALL_RAIL_TYPES.has(element.type))
    .map((rail) => ({ rail, ends: getRailEndpoints(rail) }));
  const claimed = new Set([host.id]);
  const segments = [host];
  const vertices = [hostEnds[0], hostEnds[1]];

  const grow = (forward: boolean) => {
    const heading = forward ? axis : { x: -axis.x, y: -axis.y };
    for (;;) {
      const tip = forward ? vertices[vertices.length - 1] : vertices[0];
      const touching = rails.filter(
        ({ rail, ends }) =>
          !claimed.has(rail.id) &&
          ends.some((end) => dist(end, tip) <= ENDPOINT_TOLERANCE)
      );
      // A plain split node has exactly one unclaimed continuation.
      if (touching.length !== 1) return;
      const { rail, ends } = touching[0];
      if (
        !rail.metadata.visible ||
        rail.metadata.locked ||
        resolveRailType(rail) !== 'straight' ||
        ends.length < 2
      ) {
        return;
      }
      const nearIndex =
        dist(ends[0], tip) <= dist(ends[1], tip) ? 0 : 1;
      const far = ends[nearIndex === 0 ? 1 : 0];
      const continuation = axisDirection(tip, far);
      if (
        !continuation ||
        continuation.x * heading.x + continuation.y * heading.y <
          GAP_DIRECTION_COS
      ) {
        return;
      }
      claimed.add(rail.id);
      if (forward) {
        segments.push(rail);
        vertices.push(far);
      } else {
        segments.unshift(rail);
        vertices.unshift(far);
      }
    }
  };
  grow(true);
  grow(false);
  return { segments, vertices };
}

/**
 * Retry an insert across the fewest consecutive pieces of a split straight
 * line. The hub may slide along that span just enough to fit.
 */
function planCircularJunctionInsertOnCollinearRun(
  host: LayoutElement,
  splitPoint: Pt,
  elements: LayoutElement[],
  options: InsertOnRailOptions
): InsertOnRailResult {
  const run = collectCollinearRun(host, elements);
  if (!run || run.segments.length < 2) {
    return { ok: false, reason: 'cannot-fit' };
  }
  const hostIndex = run.segments.findIndex((segment) => segment.id === host.id);
  if (hostIndex < 0) return { ok: false, reason: 'cannot-fit' };

  const dims = options.junctionDims ?? defaultJunctionDims();
  const margin = Math.max(dims.width, dims.height) / 2 + MIN_INSERT_STRAIGHT_LENGTH;
  const required = margin * 2;
  let best:
    | {
        first: number;
        last: number;
        start: Pt;
        end: Pt;
        center: Pt;
        displacement: number;
      }
    | null = null;

  for (let width = 1; width <= run.segments.length && !best; width++) {
    for (
      let first = Math.max(0, hostIndex - width + 1);
      first <= hostIndex && first + width <= run.segments.length;
      first++
    ) {
      const last = first + width - 1;
      const start = run.vertices[first];
      const end = run.vertices[last + 1];
      const axis = axisDirection(start, end);
      const length = dist(start, end);
      if (!axis || length < required) continue;
      const projected =
        (splitPoint.x - start.x) * axis.x + (splitPoint.y - start.y) * axis.y;
      const along = Math.min(Math.max(projected, margin), length - margin);
      const center = {
        x: start.x + axis.x * along,
        y: start.y + axis.y * along,
      };
      const displacement = dist(center, splitPoint);
      if (!best || displacement < best.displacement) {
        best = { first, last, start, end, center, displacement };
      }
    }
  }
  if (!best) return { ok: false, reason: 'cannot-fit' };

  const synthetic = rebuildStraightBetween(
    host,
    best.start,
    best.end,
    '__collinear_run__',
    options.lengthMetersForWidth
  );
  const planned = planCircularJunctionInsertOnStraight(
    synthetic,
    best.center,
    elements,
    options
  );
  if (!planned.ok) return planned;
  return {
    ...planned,
    hostId: host.id,
    removeIds: run.segments
      .slice(best.first, best.last + 1)
      .map((segment) => segment.id),
  };
}

type OpenStraightEnd = {
  rail: LayoutElement;
  tip: Pt;
  far: Pt;
  /** Direction in which this rail would continue beyond its open tip. */
  outward: Pt;
};

type GapFit = {
  center: Pt;
  ends: OpenStraightEnd[];
  score: number;
};

const GAP_DIRECTION_TOLERANCE_DEG = 12;
const GAP_DIRECTION_COS = Math.cos((GAP_DIRECTION_TOLERANCE_DEG * Math.PI) / 180);

function junctionCenter(el: LayoutElement): Pt {
  const offset = rotatePt(
    el.rotation || 0,
    el.dimensions.width / 2,
    el.dimensions.height / 2
  );
  return { x: el.position.x + offset.x, y: el.position.y + offset.y };
}

function intersectLines(a: OpenStraightEnd, b: OpenStraightEnd): Pt | null {
  return intersectAxes(a.tip, a.outward, b.tip, b.outward);
}

/**
 * Straight endpoints which are genuinely open. Endpoints already touching any
 * rail primitive are not eligible, so fitting a gap cannot steal an existing
 * node or branch.
 */
function collectOpenStraightEnds(
  elements: LayoutElement[],
  query: Pt,
  maxReach: number
): OpenStraightEnd[] {
  const rails = elements
    .filter((el) => ALL_RAIL_TYPES.has(el.type))
    .map((rail) => ({ rail, ends: getRailEndpoints(rail) }));
  const open: OpenStraightEnd[] = [];

  for (const { rail, ends } of rails) {
    if (
      !rail.metadata.visible ||
      rail.metadata.locked ||
      resolveRailType(rail) !== 'straight'
    ) {
      continue;
    }
    if (ends.length < 2) continue;
    for (let index = 0; index < 2; index++) {
      const tip = ends[index];
      if (dist(tip, query) > maxReach) continue;
      const far = ends[index === 0 ? 1 : 0];
      const occupied = rails.some(({ rail: other, ends: otherEnds }) => {
        if (other.id === rail.id) return false;
        return otherEnds.some(
          (otherTip) => dist(otherTip, tip) <= ENDPOINT_TOLERANCE
        );
      });
      if (occupied) continue;
      const outward = axisDirection(far, tip);
      if (outward) open.push({ rail, tip, far, outward });
    }
  }
  return open;
}

function endsThatFitCenter(
  center: Pt,
  ends: OpenStraightEnd[],
  maxReach: number,
  axisTolerance: number
): OpenStraightEnd[] {
  const ranked = ends
    .map((end) => {
      const dx = center.x - end.tip.x;
      const dy = center.y - end.tip.y;
      const forward = dx * end.outward.x + dy * end.outward.y;
      const perpendicular = Math.abs(dx * end.outward.y - dy * end.outward.x);
      return {
        end,
        forward,
        perpendicular,
        distance: Math.hypot(dx, dy),
      };
    })
    .filter(
      ({ forward, perpendicular, distance }) =>
        distance <= maxReach &&
        perpendicular <= axisTolerance &&
        // Permit a modest trim when a resized rail intrudes into the hub area.
        forward >= -maxReach * 0.35
    )
    .sort(
      (a, b) =>
        a.perpendicular - b.perpendicular ||
        Math.abs(a.forward) - Math.abs(b.forward)
    );

  // At most one rail may occupy a spoke direction and one end per source rail.
  const chosen: OpenStraightEnd[] = [];
  for (const item of ranked) {
    if (chosen.some((end) => end.rail.id === item.end.rail.id)) continue;
    const desired = axisDirection(center, item.end.tip);
    if (!desired) continue;
    const duplicatesDirection = chosen.some((end) => {
      const other = axisDirection(center, end.tip);
      return !!other && desired.x * other.x + desired.y * other.y > GAP_DIRECTION_COS;
    });
    if (!duplicatesDirection) chosen.push(item.end);
    if (chosen.length === 4) break;
  }
  return chosen;
}

function hasOppositePair(center: Pt, ends: OpenStraightEnd[]): boolean {
  const dirs = ends
    .map((end) => axisDirection(center, end.tip))
    .filter((direction): direction is Pt => !!direction);
  return dirs.some((a, index) =>
    dirs.some((b, otherIndex) => otherIndex > index && a.x * b.x + a.y * b.y < -0.9)
  );
}

/**
 * Find the best 2–4-way aligned gap around a junction drop.
 *
 * Candidate centers come from axis intersections (cross/T gaps) and midpoints
 * of opposing collinear tips (straight gaps). More attached rails always wins;
 * ties prefer the center nearest the user's drop.
 */
function findJunctionGapFit(
  worldPt: Pt,
  elements: LayoutElement[],
  dims: Dimensions,
  hitDistance: number
): GapFit | null {
  const maxReach = Math.max(hitDistance * 2, Math.max(dims.width, dims.height) * 1.5);
  const axisTolerance = Math.max(
    ENDPOINT_TOLERANCE,
    Math.min(dims.width, dims.height) * 0.1
  );
  const nearby = collectOpenStraightEnds(elements, worldPt, maxReach)
    .sort((a, b) => dist(a.tip, worldPt) - dist(b.tip, worldPt))
    .slice(0, 12);
  if (nearby.length < 2) return null;

  const candidates: Pt[] = [];
  for (let i = 0; i < nearby.length; i++) {
    for (let j = i + 1; j < nearby.length; j++) {
      if (nearby[i].rail.id === nearby[j].rail.id) continue;
      const dot =
        nearby[i].outward.x * nearby[j].outward.x +
        nearby[i].outward.y * nearby[j].outward.y;
      if (dot < -GAP_DIRECTION_COS) {
        candidates.push({
          x: (nearby[i].tip.x + nearby[j].tip.x) / 2,
          y: (nearby[i].tip.y + nearby[j].tip.y) / 2,
        });
      } else if (Math.abs(dot) < GAP_DIRECTION_COS) {
        const intersection = intersectLines(nearby[i], nearby[j]);
        if (intersection) candidates.push(intersection);
      }
    }
  }

  let best: GapFit | null = null;
  for (const center of candidates) {
    if (dist(center, worldPt) > maxReach) continue;
    const ends = endsThatFitCenter(center, nearby, maxReach, axisTolerance);
    if (ends.length < 2 || !hasOppositePair(center, ends)) continue;
    const score =
      ends.length * 1_000_000 -
      dist(center, worldPt) * 100 -
      ends.reduce((sum, end) => {
        const dx = center.x - end.tip.x;
        const dy = center.y - end.tip.y;
        return sum + Math.abs(dx * end.outward.y - dy * end.outward.x);
      }, 0);
    if (!best || score > best.score) best = { center, ends, score };
  }
  return best;
}

function makeJunctionAtCenter(
  center: Pt,
  rotation: number,
  dims: Dimensions,
  source: LayoutElement,
  activePortIds: string[],
  id: string,
  name: string
): LayoutElement {
  const centerOffset = rotatePt(rotation, dims.width / 2, dims.height / 2);
  const { sku, defaultProperties } = toolboxDefaults('rail-circle-junction');
  const layerId = source.layerId || DEFAULT_LAYER_FOR_TYPE['cross-rail'] || 'layer-1';
  return {
    id,
    type: 'cross-rail',
    layerId,
    position: {
      x: center.x - centerOffset.x,
      y: center.y - centerOffset.y,
      z: source.position.z,
    },
    rotation,
    dimensions: { ...dims, depth: source.dimensions.depth },
    metadata: {
      name,
      locked: false,
      visible: true,
      color: ELEMENT_COLORS['cross-rail'],
      layerId,
    },
    customProperties: {
      ...defaultProperties,
      sku,
      railType: 'junction-circle',
      maxPorts: INSERT_CIRCLE_JUNCTION_MAX_PORTS,
      junctionInputs: 2,
      junctionOutputs: 2,
      activePortIds,
      connections: completePortConnections(activePortIds),
      thicknessMeters:
        source.customProperties?.thicknessMeters ?? DEFAULT_TRACK_THICKNESS_METERS,
      radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
    },
  };
}

function assignGapEndsToPorts(
  center: Pt,
  ends: OpenStraightEnd[],
  dims: Dimensions,
  source: LayoutElement
): { rotation: number; assignments: { end: OpenStraightEnd; portIndex: number; tip: Pt }[] } | null {
  let best:
    | {
        rotation: number;
        error: number;
        assignments: { end: OpenStraightEnd; portIndex: number; tip: Pt }[];
      }
    | null = null;

  for (let rotation = 0; rotation < 360; rotation += 15) {
    const probe = makeJunctionAtCenter(
      center,
      rotation,
      dims,
      source,
      [],
      '__gap_probe__',
      ''
    );
    const ports = getRailEndpoints(probe);
    const used = new Set<number>();
    const assignments: { end: OpenStraightEnd; portIndex: number; tip: Pt }[] = [];
    let error = 0;
    let valid = true;

    for (const end of ends) {
      const desired = axisDirection(center, end.tip);
      if (!desired) {
        valid = false;
        break;
      }
      let match = -1;
      let matchDot = -Infinity;
      ports.forEach((tip, index) => {
        if (used.has(index)) return;
        const portDirection = axisDirection(center, tip);
        if (!portDirection) return;
        const dot = desired.x * portDirection.x + desired.y * portDirection.y;
        if (dot > matchDot) {
          matchDot = dot;
          match = index;
        }
      });
      if (match < 0 || matchDot < GAP_DIRECTION_COS) {
        valid = false;
        break;
      }
      used.add(match);
      assignments.push({ end, portIndex: match, tip: ports[match] });
      error += 1 - matchDot;
    }
    if (valid && (!best || error < best.error - EPS)) {
      best = { rotation, error, assignments };
    }
  }
  return best;
}

/**
 * Fit a Circular Junction into an empty aligned gap and reconnect 2–4 open
 * straight ends. Every touched rail is rebuilt to an exact spoke tip.
 */
export function planCircularJunctionInsertInGap(
  worldPt: Pt,
  elements: LayoutElement[],
  options: InsertOnRailOptions = {}
): InsertOnRailResult {
  const dims = options.junctionDims ?? defaultJunctionDims();
  const fit = findJunctionGapFit(
    worldPt,
    elements,
    dims,
    options.hitPerpDist ?? INSERT_HIT_PERP_DIST
  );
  if (!fit) return { ok: false, reason: 'not-on-rail' };
  if (
    nearExistingJunction(
      fit.center,
      '',
      elements,
      Math.max(dims.width, dims.height) / 2 + ENDPOINT_TOLERANCE
    )
  ) {
    return { ok: false, reason: 'near-junction' };
  }

  const source = fit.ends[0].rail;
  const assigned = assignGapEndsToPorts(fit.center, fit.ends, dims, source);
  if (!assigned) return { ok: false, reason: 'cannot-fit' };
  if (
    assigned.assignments.some(
      ({ end, tip }) => dist(end.far, tip) < MIN_INSERT_STRAIGHT_LENGTH
    )
  ) {
    return { ok: false, reason: 'too-short' };
  }

  const nextId = resolveNextId({
    ...options,
    existingIds: options.existingIds ?? elements.map((el) => el.id),
  });
  const activePortIds = assigned.assignments.map(
    ({ portIndex }) => `port-${portIndex}`
  );
  const junctionId = nextId('cross-rail', 'junction-circle');
  const junction = makeJunctionAtCenter(
    fit.center,
    assigned.rotation,
    dims,
    source,
    activePortIds,
    junctionId,
    options.nextName?.('cross-rail') ?? 'Circle Junction'
  );
  const resized = assigned.assignments.map(({ end, tip }) =>
    rebuildStraightBetween(
      end.rail,
      end.far,
      tip,
      nextId('overhead-rail', 'straight'),
      options.lengthMetersForWidth
    )
  );

  return {
    ok: true,
    hostId: source.id,
    splitPoint: fit.center,
    removeIds: fit.ends.map((end) => end.rail.id),
    addElements: [...resized, junction],
    selectId: junctionId,
  };
}

/**
 * Delete-time inverse for a simple two-sided through junction.
 *
 * Only two physically attached, opposite straight rails are eligible. Branched
 * or ambiguous junctions deliberately fall back to normal deletion.
 */
export function planDeleteSimpleThroughJunction(
  junction: LayoutElement,
  elements: LayoutElement[],
  options: InsertOnRailOptions = {}
): InsertOnRailResult {
  if (resolveRailType(junction) !== 'junction-circle') {
    return { ok: false, reason: 'on-junction' };
  }
  const configured = junction.customProperties?.activePortIds;
  if (Array.isArray(configured) && configured.length > 2) {
    return { ok: false, reason: 'cannot-fit' };
  }

  const center = junctionCenter(junction);
  const ports = getRailEndpoints(junction);
  const hits: { rail: LayoutElement; far: Pt; portIndex: number }[] = [];
  for (const rail of elements) {
    if (
      rail.id === junction.id ||
      rail.metadata.locked ||
      resolveRailType(rail) !== 'straight'
    ) {
      continue;
    }
    const ends = getRailEndpoints(rail);
    if (ends.length < 2) continue;
    for (let endIndex = 0; endIndex < 2; endIndex++) {
      const portIndex = ports.findIndex(
        (port) => dist(port, ends[endIndex]) <= ENDPOINT_TOLERANCE
      );
      if (portIndex >= 0) {
        hits.push({
          rail,
          far: ends[endIndex === 0 ? 1 : 0],
          portIndex,
        });
        break;
      }
    }
  }
  if (hits.length !== 2 || hits[0].rail.id === hits[1].rail.id) {
    return { ok: false, reason: 'cannot-fit' };
  }
  const p0 = axisDirection(center, ports[hits[0].portIndex]);
  const p1 = axisDirection(center, ports[hits[1].portIndex]);
  if (!p0 || !p1 || p0.x * p1.x + p0.y * p1.y > -0.9) {
    return { ok: false, reason: 'cannot-fit' };
  }
  if (
    resolveTrackDirection(hits[0].rail) === 'uni' &&
    resolveTrackDirection(hits[1].rail) === 'uni'
  ) {
    const r0 = ((hits[0].rail.rotation || 0) * Math.PI) / 180;
    const r1 = ((hits[1].rail.rotation || 0) * Math.PI) / 180;
    if (Math.cos(r0) * Math.cos(r1) + Math.sin(r0) * Math.sin(r1) < 0.9) {
      return { ok: false, reason: 'cannot-fit' };
    }
  }
  const mergedLength = dist(hits[0].far, hits[1].far);
  if (mergedLength < MIN_INSERT_STRAIGHT_LENGTH) {
    return { ok: false, reason: 'too-short' };
  }

  const source =
    resolveTrackDirection(hits[0].rail) === 'uni'
      ? hits[0].rail
      : resolveTrackDirection(hits[1].rail) === 'uni'
        ? hits[1].rail
        : hits[0].rail.dimensions.width >= hits[1].rail.dimensions.width
          ? hits[0].rail
          : hits[1].rail;
  const nextId = resolveNextId({
    ...options,
    existingIds: options.existingIds ?? elements.map((el) => el.id),
  });
  const merged = rebuildStraightBetween(
    source,
    hits[0].far,
    hits[1].far,
    nextId('overhead-rail', 'straight'),
    options.lengthMetersForWidth
  );
  return {
    ok: true,
    hostId: junction.id,
    splitPoint: center,
    removeIds: [junction.id, hits[0].rail.id, hits[1].rail.id],
    addElements: [merged],
    selectId: merged.id,
  };
}

/**
 * Replace a curve turn with a Circular Junction at the Manhattan corner.
 *
 * Adjacent straights that met the curve tips are rebuilt to meet the junction
 * spoke tips, preserving the existing path on two ports and leaving the rest
 * free for new branches (Point Draw / further drops).
 */
export function planCircularJunctionInsertOnCurve(
  host: LayoutElement,
  elements: LayoutElement[],
  options: InsertOnRailOptions = {}
): InsertOnRailResult {
  const rt = resolveRailType(host);
  if (!isCurveRailType(rt)) {
    return { ok: false, reason: 'not-on-rail' };
  }

  const tangents = curveEndpointTangents(host);
  if (tangents.length < 2) return { ok: false, reason: 'not-on-rail' };

  // Each leg is the axis the track actually runs along on that side: the
  // neighbouring straight's own centerline when one abuts the curve tip,
  // otherwise the curve's exit tangent. Rebuilt rails are generated along these
  // axes, so they can never pick up a tilt to reach the junction.
  //
  // The neighbour search scales with the curve (its tips are one radius from the
  // corner) rather than using the endpoint-clustering constant, so a small
  // clustering tolerance cannot silently leave the old straights behind.
  const neighbourTolerance = Math.max(
    ENDPOINT_TOLERANCE,
    getCurveRadius(host.dimensions.width, host.dimensions.height)
  );
  const claimed = new Set<string>();
  const legs = tangents.map((tangent) => {
    const abut = findAbuttingStraight(
      tangent.pt,
      host.id,
      elements,
      neighbourTolerance,
      claimed
    );
    if (abut) claimed.add(abut.rail.id);
    const dir = abut
      ? axisDirection(abut.nearEnd, abut.farEnd)
      : axisDirection(tangent.pt, {
          x: tangent.pt.x + Math.cos((tangent.outwardDeg * Math.PI) / 180),
          y: tangent.pt.y + Math.sin((tangent.outwardDeg * Math.PI) / 180),
        });
    const origin = abut ? abut.farEnd : tangent.pt;
    return { abut, dir, origin };
  });
  if (legs.some((leg) => !leg.dir)) return { ok: false, reason: 'cannot-fit' };

  // The hub sits where the two leg axes meet — the corner the operator sees.
  const corner =
    intersectAxes(legs[0].origin, legs[0].dir!, legs[1].origin, legs[1].dir!) ??
    curveCornerVertex(host);
  if (!corner) {
    // Parallel axes (e.g. a curve180 U-turn) have no corner to host a junction.
    return { ok: false, reason: 'cannot-fit' };
  }

  const dims = options.junctionDims ?? defaultJunctionDims();
  const half = Math.max(dims.width, dims.height) / 2;
  const clearance = half + ENDPOINT_TOLERANCE;
  if (nearExistingJunction(corner, host.id, elements, clearance)) {
    return { ok: false, reason: 'near-junction' };
  }

  // Rotate the hub so a spoke lies on the first leg's axis; remaining ports
  // fall on 45° increments (8-way), so Manhattan turns still land on cardinals.
  const maxPorts = INSERT_CIRCLE_JUNCTION_MAX_PORTS;
  const rotation = normDeg(
    (Math.atan2(legs[0].dir!.y, legs[0].dir!.x) * 180) / Math.PI
  );
  const centerOffset = rotatePt(rotation, dims.width / 2, dims.height / 2);
  const position = { x: corner.x - centerOffset.x, y: corner.y - centerOffset.y };

  const { sku, defaultProperties } = toolboxDefaults('rail-circle-junction');
  const layerId = host.layerId || DEFAULT_LAYER_FOR_TYPE['cross-rail'] || 'layer-1';
  const nextName =
    options.nextName ??
    ((type: ElementType) => (type === 'cross-rail' ? 'Circle Junction' : 'Rail'));
  const nextId = resolveNextId({
    ...options,
    existingIds: options.existingIds ?? elements.map((el) => el.id),
  });

  const hostProps = host.customProperties || {};

  const junctionId = nextId('cross-rail', 'junction-circle');
  const junction: LayoutElement = {
    id: junctionId,
    type: 'cross-rail',
    layerId,
    position: { ...position, z: host.position.z },
    rotation,
    dimensions: { ...dims, depth: host.dimensions.depth },
    metadata: {
      name: nextName('cross-rail'),
      locked: false,
      visible: true,
      color: ELEMENT_COLORS['cross-rail'],
      layerId,
    },
    customProperties: {
      ...defaultProperties,
      sku,
      railType: 'junction-circle',
      maxPorts,
      junctionInputs: 2,
      junctionOutputs: 2,
      activePortIds: [] as string[],
      connections: [] as { id: string; fromPortId: string; toPortId: string }[],
      thicknessMeters: hostProps.thicknessMeters ?? DEFAULT_TRACK_THICKNESS_METERS,
      radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
    },
  };

  // Assign each leg the spoke pointing along it, then keep the existing path
  // connected through that pair. Remaining ports stay free for new branches.
  const spokeTips = getRailEndpoints(junction);
  const usedPorts = new Set<number>();
  const portForLeg = legs.map((leg) => {
    let bestIndex = -1;
    let bestDot = -Infinity;
    spokeTips.forEach((tip, index) => {
      if (usedPorts.has(index)) return;
      const toTip = axisDirection(corner, tip);
      if (!toTip) return;
      const dot = toTip.x * leg.dir!.x + toTip.y * leg.dir!.y;
      if (dot > bestDot) {
        bestDot = dot;
        bestIndex = index;
      }
    });
    if (bestIndex >= 0) usedPorts.add(bestIndex);
    return bestIndex;
  });
  if (portForLeg.some((index) => index < 0)) {
    return { ok: false, reason: 'cannot-fit' };
  }

  const activePortIds = portForLeg.map((index) => `port-${index}`);
  const connections = [
    {
      id: generateId(),
      fromPortId: activePortIds[0],
      toPortId: activePortIds[1],
    },
    {
      id: generateId(),
      fromPortId: activePortIds[1],
      toPortId: activePortIds[0],
    },
  ];
  junction.customProperties = {
    ...junction.customProperties,
    activePortIds,
    connections,
  };

  const thickness = Math.max(0.001, host.dimensions.height || 4);
  const sizing: ChainSizing = {
    lengthUnits: 1,
    thicknessUnits: thickness,
    radiusUnits: thickness,
  };

  const removeIds = new Set<string>([host.id]);
  const addElements: LayoutElement[] = [junction];

  for (let i = 0; i < legs.length; i++) {
    const { abut, dir, origin } = legs[i];
    if (!abut || !dir) continue;

    // Land the rebuilt rail on its own axis (the port projected onto the leg),
    // never on the raw spoke tip — an off-axis target would skew the rail.
    const port = spokeTips[portForLeg[i]];
    const along = (port.x - origin.x) * dir.x + (port.y - origin.y) * dir.y;
    const end = {
      x: origin.x + dir.x * along,
      y: origin.y + dir.y * along,
    };

    if (dist(abut.farEnd, end) < MIN_INSERT_STRAIGHT_LENGTH) {
      return { ok: false, reason: 'too-short' };
    }

    const abutProps = abut.rail.customProperties || {};
    const abutLengthMetersForWidth =
      options.lengthMetersForWidth ??
      ((w: number) => {
        const hostLen = Number(abutProps.lengthMeters);
        const hostW = abut.rail.dimensions.width || 1;
        if (Number.isFinite(hostLen) && hostLen > 0 && hostW > 0) {
          return (hostLen * w) / hostW;
        }
        return w;
      });

    const geom = computeSegmentBetween(abut.farEnd, end, {
      ...sizing,
      thicknessUnits: Math.max(0.001, abut.rail.dimensions.height || thickness),
    });
    removeIds.add(abut.rail.id);
    addElements.push(
      inheritTravelDirection(
        {
          id: nextId('overhead-rail', 'straight'),
          type: 'overhead-rail',
          layerId: abut.rail.layerId,
          position: { ...geom.position, z: abut.rail.position.z },
          rotation: geom.rotation,
          dimensions: {
            ...geom.dimensions,
            depth: abut.rail.dimensions.depth,
          },
          metadata: {
            name: abut.rail.metadata.name || nextName('overhead-rail'),
            locked: false,
            visible: true,
            color: abut.rail.metadata.color || ELEMENT_COLORS['overhead-rail'],
            layerId: abut.rail.layerId,
          },
          customProperties: {
            sku: abutProps.sku ?? hostProps.sku ?? 'AeroTrak',
            railType: 'straight',
            direction: abutProps.direction ?? hostProps.direction ?? DEFAULT_TRACK_DIRECTION,
            heightM: abutProps.heightM ?? 3.2,
            thicknessMeters:
              abutProps.thicknessMeters ??
              hostProps.thicknessMeters ??
              DEFAULT_TRACK_THICKNESS_METERS,
            lengthMeters: abutLengthMetersForWidth(geom.dimensions.width),
          },
        },
        abut.rail
      )
    );
  }

  return {
    ok: true,
    hostId: host.id,
    splitPoint: corner,
    removeIds: Array.from(removeIds),
    addElements,
    selectId: junctionId,
  };
}

/**
 * Point Draw: convert a curve under the cursor into a Circular Junction and
 * attach the current head to a free branch port (T-junction into the turn).
 */
export function planPointDrawCurveJunctionInsert(
  host: LayoutElement,
  start: Pt,
  elements: LayoutElement[],
  options: InsertOnRailOptions = {}
): PerpendicularCrossingInsertResult {
  const base = planCircularJunctionInsertOnCurve(host, elements, options);
  if (!base.ok) return base;

  const junction = base.addElements.find((element) => element.id === base.selectId);
  if (!junction) return { ok: false, reason: 'cannot-fit' };

  const hostPortIds = new Set(
    Array.isArray(junction.customProperties?.activePortIds)
      ? (junction.customProperties.activePortIds as string[])
      : []
  );

  const branchPorts = getRailEndpoints(junction)
    .map((tip, index) => ({ tip, index, id: `port-${index}` }))
    .filter((port) => !hostPortIds.has(port.id));
  if (branchPorts.length === 0) return { ok: false, reason: 'cannot-fit' };

  // Prefer a free port that shares an axis with the approach head and sits on
  // the outward side of that axis; fall back to nearest free port.
  const axisTol = ENDPOINT_TOLERANCE;
  const ranked = [...branchPorts].sort((a, b) => {
    const axisA =
      Math.abs(start.x - a.tip.x) <= axisTol || Math.abs(start.y - a.tip.y) <= axisTol
        ? 0
        : 1;
    const axisB =
      Math.abs(start.x - b.tip.x) <= axisTol || Math.abs(start.y - b.tip.y) <= axisTol
        ? 0
        : 1;
    if (axisA !== axisB) return axisA - axisB;
    return dist(start, a.tip) - dist(start, b.tip);
  });
  const entry = ranked[0];
  const exit = ranked[1] ?? entry;

  if (dist(start, entry.tip) < MIN_INSERT_STRAIGHT_LENGTH) {
    return { ok: false, reason: 'cannot-fit' };
  }

  const activePortIds = [...hostPortIds, entry.id];
  const connections: { id: string; fromPortId: string; toPortId: string }[] = [];
  activePortIds.forEach((fromPortId) => {
    activePortIds.forEach((toPortId) => {
      if (fromPortId !== toPortId) {
        connections.push({ id: generateId(), fromPortId, toPortId });
      }
    });
  });

  junction.customProperties = {
    ...junction.customProperties,
    activePortIds,
    connections,
  };

  return {
    ...base,
    entryPort: entry.tip,
    exitPort: exit.tip,
    continuesBeyond: false,
  };
}

/**
 * Plans a Circular Junction where an axis-aligned Point Draw leg meets an
 * existing perpendicular straight. The host split is delegated to the normal
 * edge-insertion planner.
 *
 * - Full cross (`continuesBeyond`): caller adds approach + exit stubs.
 * - Click-on-host (`!continuesBeyond`): caller adds only the approach stub and
 *   resumes Point Draw from the unused opposite port.
 */
export function planPerpendicularCircularJunctionInsert(
  host: LayoutElement,
  start: Pt,
  end: Pt,
  elements: LayoutElement[],
  options: InsertOnRailOptions = {}
): PerpendicularCrossingInsertResult {
  const drawAxis = segmentAxis(start, end);
  const hostEnds = getRailEndpoints(host);
  if (!drawAxis || hostEnds.length < 2) return { ok: false, reason: 'not-on-rail' };

  const hostAxis = segmentAxis(hostEnds[0], hostEnds[1]);
  if (!hostAxis || hostAxis === drawAxis || resolveRailType(host) !== 'straight') {
    return { ok: false, reason: 'not-on-rail' };
  }

  const crossing = findPerpendicularStraightCrossing(start, end, [host]);
  if (!crossing) return { ok: false, reason: 'not-on-rail' };

  const base = planCircularJunctionInsertOnStraight(
    host,
    crossing.intersection,
    elements,
    options
  );
  if (!base.ok) return base;

  const junction = base.addElements.find((element) => element.id === base.selectId);
  if (!junction) return { ok: false, reason: 'cannot-fit' };

  const maxPorts = Number(junction.customProperties?.maxPorts);
  if (!Number.isFinite(maxPorts) || maxPorts < 4) {
    return { ok: false, reason: 'cannot-fit' };
  }

  const hostPortIds = new Set(
    Array.isArray(junction.customProperties?.activePortIds)
      ? (junction.customProperties.activePortIds as string[])
      : []
  );
  const drawDir = axisDirection(start, end);
  const freePorts = getRailEndpoints(junction)
    .map((tip, index) => ({ tip, index, id: `port-${index}` }))
    .filter((port) => !hostPortIds.has(port.id))
    .map((port) => {
      const toTip = axisDirection(crossing.intersection, port.tip);
      const align =
        toTip && drawDir
          ? Math.abs(toTip.x * drawDir.x + toTip.y * drawDir.y)
          : 0;
      return { ...port, align, distStart: dist(start, port.tip) };
    })
    // Prefer the free spokes that lie on the draw axis (cardinals on an 8-way).
    .sort((a, b) => b.align - a.align || a.distStart - b.distStart);

  const crossingPorts = freePorts.filter((port) => port.align >= 0.85).slice(0, 2);
  if (crossingPorts.length !== 2) return { ok: false, reason: 'cannot-fit' };

  const firstIsEntry =
    dist(start, crossingPorts[0].tip) <= dist(start, crossingPorts[1].tip);
  const entry = firstIsEntry ? crossingPorts[0] : crossingPorts[1];
  const exit = firstIsEntry ? crossingPorts[1] : crossingPorts[0];

  const approachLen = dist(start, crossing.intersection);
  const approachNeed = dist(entry.tip, crossing.intersection) + MIN_INSERT_STRAIGHT_LENGTH;
  if (approachLen < approachNeed) {
    return { ok: false, reason: 'cannot-fit' };
  }

  const beyondLen = dist(end, crossing.intersection);
  const exitNeed = dist(exit.tip, crossing.intersection) + MIN_INSERT_STRAIGHT_LENGTH;
  const continuesBeyond = beyondLen >= exitNeed;

  // Only the through pair + branch entry/exit — unused diagonal spokes stay free.
  const activePortIds = continuesBeyond
    ? [...hostPortIds, entry.id, exit.id]
    : [...hostPortIds, entry.id];
  const connections: { id: string; fromPortId: string; toPortId: string }[] = [];
  activePortIds.forEach((fromPortId) => {
    activePortIds.forEach((toPortId) => {
      if (fromPortId !== toPortId) {
        connections.push({ id: generateId(), fromPortId, toPortId });
      }
    });
  });

  junction.customProperties = {
    ...junction.customProperties,
    activePortIds,
    connections,
  };

  return {
    ...base,
    entryPort: entry.tip,
    exitPort: exit.tip,
    continuesBeyond,
  };
}

/**
 * Top-level insert entry. Extensible for future payload kinds / host kinds.
 */
export function tryInsertOnRail(
  worldPt: Pt,
  elements: LayoutElement[],
  payload: InsertPayloadKind = 'circular-junction-through',
  options: InsertOnRailOptions = {}
): InsertOnRailResult {
  if (payload !== 'circular-junction-through') {
    // Reserved for merge/split / branched CJ — not implemented yet.
    return { ok: false, reason: 'not-on-rail' };
  }

  const hitPerpDist = options.hitPerpDist ?? INSERT_HIT_PERP_DIST;
  const opts: InsertOnRailOptions = {
    ...options,
    existingIds: options.existingIds ?? elements.map((el) => el.id),
  };
  const hostInfo = findInsertHost(worldPt, elements, hitPerpDist);
  if (hostInfo?.kind === 'junction') return { ok: false, reason: 'on-junction' };

  // A turn wins whenever its corner is nearer than the straight under the
  // cursor: close to a corner the adjacent straights are the nearest rails, so
  // classifying by centerline alone would make turns unreachable.
  const turn = findCurveTurnNear(worldPt, elements, hitPerpDist);
  const straightDist =
    hostInfo?.kind === 'straight' ? dist(worldPt, hostInfo.split) : Infinity;
  if (turn && (hostInfo?.kind === 'curve' || turn.distance <= straightDist)) {
    return planCircularJunctionInsertOnCurve(turn.host, elements, opts);
  }

  if (!hostInfo) {
    return planCircularJunctionInsertInGap(worldPt, elements, opts);
  }
  if (hostInfo.kind === 'curve') {
    return planCircularJunctionInsertOnCurve(hostInfo.host, elements, opts);
  }

  const direct = planCircularJunctionInsertOnStraight(
    hostInfo.host,
    hostInfo.split,
    elements,
    opts
  );
  if (
    !direct.ok &&
    (direct.reason === 'on-endpoint' ||
      direct.reason === 'too-short' ||
      direct.reason === 'cannot-fit')
  ) {
    const run = planCircularJunctionInsertOnCollinearRun(
      hostInfo.host,
      hostInfo.split,
      elements,
      opts
    );
    if (run.ok) return run;
    const gap = planCircularJunctionInsertInGap(worldPt, elements, opts);
    if (gap.ok) return gap;
  }
  return direct;
}

/** True when a toolbox item is the Circular Junction catalog entry. */
export function isCircleJunctionToolboxItem(item: ToolboxItem | null | undefined): boolean {
  if (!item) return false;
  return (
    item.type === 'cross-rail' &&
    (item.defaultProperties as { railType?: string } | undefined)?.railType === 'junction-circle'
  );
}

export function insertRejectLabel(reason: InsertRejectReason): string {
  switch (reason) {
    case 'on-curve':
      return 'Cannot insert on this curve';
    case 'on-junction':
      return 'Cannot insert on a junction';
    case 'on-endpoint':
      return 'Too close to an endpoint';
    case 'too-short':
    case 'cannot-fit':
      return 'Track too short for junction';
    case 'near-junction':
      return 'Too close to an existing junction';
    case 'not-on-rail':
    default:
      return 'Invalid placement';
  }
}
